import styled, { css, keyframes } from "styled-components";

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const SectionHeader = styled.div`
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--accent-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
`;

export const SectionActions = styled.div`
  display: flex;
  gap: 15px;
`;

export const Button = styled.button`
  padding: 12px 25px;
  border: none;
  border-radius: 20px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  ${(props) =>
    props.$small &&
    `
    padding: 8px 16px;
    font-size: 12px;
  `}

  ${(props) =>
    props.$primary &&
    `
    background: var(--gradient-primary);
    color: var(--surface);

    &:hover:not(:disabled) {
      background: var(--accent-dark);
      color: var(--surface);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$secondary &&
    `
    background: transparent;
    color: var(--text-secondary);
    border: 2px solid var(--accent-light);

    &:hover:not(:disabled) {
      background: var(--accent-light);
      border-color: var(--accent);
      color: var(--accent);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$danger &&
    `
    background: #dc3545;
    color: white;

    &:hover:not(:disabled) {
      background: #c82333;
      transform: translateY(-2px);
    }
  `}

  svg {
    animation: ${(props) =>
      props.$loading
        ? css`
            ${spin} 1s ease-in-out infinite
          `
        : "none"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

export const SearchInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 20px;
  font-family: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

export const DataTableScroll = styled.div`
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: visible !important;
  border-radius: 15px;
  box-shadow: var(--shadow-soft);
  max-width: none !important;
  min-width: 0 !important;
  flex-shrink: 0 !important;
`;

export const DataTable = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 1fr 120px;
  min-width: 750px;
  background: var(--surface);
  border-radius: 15px;
  border: 2px solid var(--accent-light);
  overflow: hidden;
`;

export const TableHeader = styled.div`
  padding: 15px 12px;
  border-bottom: 2px solid var(--accent-light);
  background-color: var(--accent);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 1px;
  color: white;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$align || "left"};
`;
export const CourseTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

export const CourseCategory = styled.div`
  font-size: 11px;
  color: var(--text-muted);
`;
export const TableRow = styled.div`
  display: contents;
`;
export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
export const CourseInfo = styled.div``;
export const CourseIcon = styled.div`
  width: 40px;
  height: 40px;
  background: var(--gradient-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--surface);
`;

export const TableCell = styled.div`
  white-space: nowrap;
  padding: 15px 12px;
  border-bottom: 1px solid var(--accent-light);
  display: flex;
  align-items: center;
  text-align: ${(props) => props.$align || "left"};
  font-size: 14px;
  color: var(--text-primary);
`;

export const CourseTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

export const CourseTag = styled.span`
  background: var(--accent-light);
  color: var(--accent);
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

export const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  ${(props) =>
    props.$active
      ? `
    background: #d4edda;
    color: #155724;
  `
      : `
    background: #f8d7da;
    color: #721c24;
  `}
`;

export const EmptyMessage = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px;
  color: var(--text-muted);
  font-size: 14px;
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

export const PageInfo = styled.span`
  color: var(--text-muted);
  font-size: 14px;
`;
